export function TradingShellScreen(input: { asset: string; message: string }) {
  return (
    <div className="ws-shell ws-shell-loading">
      <div className="ws-global-nav">
        <div className="ws-global-nav-left">
          <span className="ws-brand-logo">
            <span className="ws-brand-primary">hypersense</span>
          </span>
        </div>
        <div className="ws-global-nav-right">
          <button className="ws-connect-btn" disabled type="button">
            <span className="ws-connect-btn-label ws-connect-btn-label-desktop">
              Connect Wallet
            </span>
            <span className="ws-connect-btn-label ws-connect-btn-label-mobile">
              Connect
            </span>
          </button>
        </div>
      </div>

      <div className="ws-workspace">
        {/* Desktop top bar — hidden at ≤760px */}
        <div className="ws-top-bar ws-shell-top-bar-desktop">
          <ShellAssetTrigger asset={input.asset} />
          <div className="ws-stats-bar">
            <ShellStatSkeleton isEmphasized label="Mark" valueWidthPx={84} />
            <ShellStatSkeleton label="Oracle" valueWidthPx={84} />
            <ShellStatSkeleton label="24h Change" valueWidthPx={72} />
            <ShellStatSkeleton label="24h Volume" valueWidthPx={76} />
            <ShellStatSkeleton label="Open Interest" valueWidthPx={84} />
            <ShellStatSkeleton label="Funding / Countdown" valueWidthPx={120} />
          </div>
        </div>

        {/* Mobile top bar — hidden at >760px */}
        <div className="ws-top-bar ws-top-bar-mobile ws-shell-top-bar-mobile">
          <div className="ws-mobile-market-card">
            <div className="ws-mobile-market-card-row">
              <ShellAssetTrigger asset={input.asset} isMobile={true} />
              <div className="ws-mobile-market-summary">
                <span className="ws-stat-skeleton is-wide is-large" />
                <span className="ws-stat-skeleton" />
              </div>
              <div className="ws-shell-toggle-placeholder" />
            </div>
          </div>
        </div>

        {/* Desktop body — hidden at ≤1024px */}
        <div className="ws-body ws-shell-body-desktop">
          <div className="ws-main ws-main-workbench">
            <div className="ws-surface-panel ws-surface-panel-fill">
              <div className="ws-chart-panel">
                <div className="ws-chart-surface">
                  <div className="ws-chart-header">
                    <div className="ws-chart-header-main">
                      <div className="ws-chart-header-group ws-chart-header-group-timeframe">
                        <div className="ws-chart-timeframe-rail">
                          <span
                            className="ws-skeleton-bar"
                            style={{ width: 120 }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="ws-chart-surface-body">
                    <ShellChartGrid />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <aside className="ws-sidebar">
            <div className="ws-sidebar-tabs-root">
              <div className="ws-sidebar-tabs">
                <span className="ws-sidebar-tab" data-state="active">
                  Order Book
                </span>
                <span className="ws-sidebar-tab" data-state="inactive">
                  Trades
                </span>
              </div>
              <div className="ws-sidebar-content" data-state="active">
                <ShellSidebarSkeleton />
              </div>
            </div>
          </aside>
        </div>

        {/* Mobile body — hidden at >1024px */}
        <div className="ws-mobile-shell ws-shell-body-mobile">
          <div className="ws-mobile-surface-tabs">
            <span className="ws-mobile-surface-tab" data-state="active">
              Chart
            </span>
            <span className="ws-mobile-surface-tab" data-state="inactive">
              Order Book
            </span>
            <span className="ws-mobile-surface-tab" data-state="inactive">
              Trades
            </span>
          </div>
          <div className="ws-mobile-surface-panel" data-state="active">
            <div className="ws-surface-panel ws-surface-panel-fill">
              <ShellChartGrid />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ShellAssetTrigger(input: { asset: string; isMobile?: boolean }) {
  const className = input.isMobile
    ? 'ws-asset-trigger ws-shell-asset-trigger mobile-summary'
    : 'ws-asset-trigger ws-shell-asset-trigger';

  return (
    <div aria-hidden={true} className={className}>
      <span className="ws-asset-trigger-glyph ws-shell-asset-avatar" />
      <span className="ws-asset-trigger-copy">
        <span className="ws-asset-trigger-primary-row">
          <span className="ws-asset-trigger-primary">{input.asset}-USDC</span>
          <span className="ws-shell-asset-leverage" />
        </span>
      </span>
      <span className="ws-asset-icon ws-shell-asset-chevron" />
    </div>
  );
}

function ShellStatSkeleton(input: {
  isEmphasized?: boolean;
  label: string;
  valueWidthPx?: number;
}) {
  const emphasisClassName = input.isEmphasized ? ' is-emphasized' : '';
  const skeletonClassName = input.isEmphasized
    ? 'ws-stat-skeleton is-wide is-large'
    : 'ws-stat-skeleton is-wide';
  const skeletonStyle =
    input.valueWidthPx === undefined
      ? undefined
      : { width: input.valueWidthPx };

  return (
    <div className={`ws-stat-stack${emphasisClassName}`}>
      <span className="ws-stat-label">{input.label}</span>
      <span className={skeletonClassName} style={skeletonStyle} />
    </div>
  );
}

const SHELL_GRID_ROWS = [0, 1, 2, 3, 4, 5];

function ShellChartGrid() {
  return (
    <div className="ws-shell-chart-bg">
      <div className="ws-chart-placeholder-grid">
        {SHELL_GRID_ROWS.map((i) => (
          <div className="ws-chart-placeholder-gridline" key={i} />
        ))}
      </div>
    </div>
  );
}

const SHELL_SIDEBAR_ROW_IDS = [1, 2, 3, 4, 5, 6, 7, 8] as const;

function ShellSidebarSkeleton() {
  return (
    <section className="ws-side-panel ws-side-panel-order-book">
      <div className="ws-side-panel-toolbar">
        <span className="ws-skeleton-bar" style={{ width: 72 }} />
        <span className="ws-skeleton-bar" style={{ width: 56 }} />
      </div>
      <div className="ws-side-panel-header">
        <span>Price</span>
        <span>Size</span>
        <span>Total</span>
      </div>
      <div className="ws-side-panel-scroll">
        <div className="ws-orderbook">
          {SHELL_SIDEBAR_ROW_IDS.map((rowId) => (
            <div className="ws-ob-row" key={rowId}>
              <span className="ws-skeleton-bar" style={{ width: 54 }} />
              <span className="ws-skeleton-bar" style={{ width: 44 }} />
              <span className="ws-skeleton-bar" style={{ width: 52 }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
